import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Label,
  Customized,          // ✅ ADD THIS
} from "recharts";
import { useDirectory } from "./context/DirectoryContext";

const COLORS = [
  "#2563eb", // blue-600
  "#16a34a", // green-600
  "#f59e0b", // amber-500
  "#dc2626", // red-600
  "#7c3aed", // violet-600
  "#0891b2", // cyan-600
  "#ea580c", // orange-600
  "#db2777", // pink-600
];

const timeFrames = {
  '1M': 22,
  '6M': 132,
  '1Y': 252,
  '3Y': 252 * 3,
  '5Y': 252 * 5,
  '10Y': 252 * 10,
  'Max': Infinity
};


// Compute Pearson correlation of two numeric arrays
const pearsonCorrelation = (xs, ys) => {
  const n = xs.length;
  if (n < 2) return null;

  let sumX = 0, sumY = 0;
  for (let i = 0; i < n; i++) {
    sumX += xs[i];
    sumY += ys[i];
  }
  const meanX = sumX / n;
  const meanY = sumY / n;

  let num = 0, denomX = 0, denomY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denomX += dx * dx;
    denomY += dy * dy;
  }

  if (denomX === 0 || denomY === 0) return null;
  return num / Math.sqrt(denomX * denomY);
};

// Build daily return series from rebased prices and compute correlation
const computeCorrelationFromRows = (rows, symA, symB) => {
  const retA = [];
  const retB = [];

  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1];
    const cur = rows[i];

    const aPrev = prev[symA];
    const aCur = cur[symA];
    const bPrev = prev[symB];
    const bCur = cur[symB];

    if (
      typeof aPrev === "number" &&
      typeof aCur === "number" &&
      typeof bPrev === "number" &&
      typeof bCur === "number" &&
      !Number.isNaN(aPrev) &&
      !Number.isNaN(aCur) &&
      !Number.isNaN(bPrev) &&
      !Number.isNaN(bCur) &&
      aPrev !== 0 &&
      bPrev !== 0
    ) {
      // % daily returns based on rebased index values
      const rA = ((aCur / aPrev) - 1) * 100;
      const rB = ((bCur / bPrev) - 1) * 100;
      retA.push(rA);
      retB.push(rB);
    }
  }

  if (retA.length < 5) return null; // avoid noisy values
  return pearsonCorrelation(retA, retB);
};

const formatCorrelation = (val) => {
  if (val === null || Number.isNaN(val)) return "N/A";
  return val.toFixed(2);
};

// Tailwind text color based on correlation strength
const correlationColorClass = (val) => {
  if (val === null || Number.isNaN(val)) return "text-gray-500";

  if (val >= 0.8) return "text-green-700";
  if (val >= 0.6) return "text-green-600";
  if (val >= 0.4) return "text-green-500";
  if (val <= -0.8) return "text-red-700";
  if (val <= -0.6) return "text-red-600";
  if (val <= -0.4) return "text-red-500";
  return "text-gray-700";
};

// Background pill for the overall correlation badge
const correlationBadgeClass = (val) => {
  if (val === null || Number.isNaN(val))
    return "bg-gray-100 text-gray-600";

  if (val >= 0.8) return "bg-green-100 text-green-800";
  if (val >= 0.6) return "bg-emerald-100 text-emerald-800";
  if (val >= 0.4) return "bg-lime-100 text-lime-800";
  if (val <= -0.8) return "bg-red-100 text-red-800";
  if (val <= -0.6) return "bg-rose-100 text-rose-800";
  if (val <= -0.4) return "bg-orange-100 text-orange-800";
  return "bg-gray-100 text-gray-800";
};


function Playground() {
  const [symbolsInput, setSymbolsInput] = useState("");
  const [chartData, setChartData] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRange, setSelectedRange] = useState('3Y');
  const [manualMode, setManualMode] = useState(false); // ✅ checkbox toggle
  const [pairMode, setPairMode] = useState("ratio"); 
  const { directory, loading: dirLoading } = useDirectory();

  // --- Directory search dropdown state ---
  const [q, setQ] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const sugRef = useRef(null);

  // Parse current comma-separated input into chips
  const selectedSymbols = useMemo(() => {
    return symbolsInput
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
  }, [symbolsInput]);

  const addSymbolToInput = (symbol) => {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) return;

    // prevent duplicates
    const set = new Set(selectedSymbols);
    set.add(sym);

    setSymbolsInput(Array.from(set).join(", "));
    setQ("");
    setOpenSug(false);
    setActiveIdx(0);
  };

  const removeSymbolFromInput = (symbol) => {
    const sym = (symbol || "").trim().toUpperCase();
    const next = selectedSymbols.filter((s) => s !== sym);
    setSymbolsInput(next.join(", "));
  };

  // Convert directory map -> entries
  const entries = useMemo(() => {
    return Object.entries(directory || {}).map(([symbol, obj]) => ({
      symbol: symbol.trim().toUpperCase(),
      name: (obj?.name || "").trim(),
      quoteType: (obj?.quoteType || "").trim(),
    }));
  }, [directory]);

  // Suggestions (simple scoring)
  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];

    const selectedSet = new Set(selectedSymbols);

    const scored = [];
    for (const e of entries) {
      if (selectedSet.has(e.symbol)) continue; // hide already selected

      const sym = e.symbol.toLowerCase();
      const nm = e.name.toLowerCase();

      let score = 0;
      if (sym === s) score = 100;
      else if (sym.startsWith(s)) score = 85;
      else if (nm.startsWith(s)) score = 70;
      else if (nm.includes(s) || sym.includes(s)) score = 50;
      else continue;

      scored.push({ e, score });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 8).map((x) => x.e);
  }, [q, entries, selectedSymbols]);

  const onKeyDown = (e) => {
    if (!openSug || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      // If dropdown open and has selection -> pick it
      e.preventDefault();
      const chosen = suggestions[activeIdx];
      if (chosen) addSymbolToInput(chosen.symbol);
    } else if (e.key === "Escape") {
      setOpenSug(false);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (event) => {
      if (
        sugRef.current &&
        inputRef.current &&
        !sugRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setOpenSug(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLoad = async () => {
    const rawSymbols = symbolsInput
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);

    if (!rawSymbols.length) {
      setError("Please enter at least one symbol.");
      return;
    }

    setError("");
    setLoading(true);
    setChartData([]);
    setSymbols([]);

    try {
      // Fetch all symbols in parallel
      const responses = await Promise.all(
        rawSymbols.map((symbol) =>
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/candles/${symbol}`
          )
        )
      );

      // Map: symbol -> { date: [...], close: [...] }
      const symbolSeries = {};
      rawSymbols.forEach((symbol, idx) => {
        const data = responses[idx].data || responses[idx]; // depending on your API shape
        const dates = data.date || [];
        const closes = data.close || [];

        if (!dates.length || !closes.length) {
          console.warn(`No data for ${symbol}`);
          return;
        }

        // Build map date -> raw close (no normalisation here)
        const map = {};
        dates.forEach((d, i) => {
          const c = Number(closes[i]);
          map[d] = Number.isFinite(c) ? c : null;
        });

        symbolSeries[symbol] = { dates, map };
      });

      const validSymbols = Object.keys(symbolSeries);
      if (!validSymbols.length) {
        setError("No valid data returned for the given symbols.");
        setLoading(false);
        return;
      }

      // Build intersection of dates across all symbols,
      // based on the first valid symbol as baseline.
      const baseSymbol = validSymbols[0];
      let commonDates = [...symbolSeries[baseSymbol].dates];

      for (let i = 1; i < validSymbols.length; i++) {
        const sym = validSymbols[i];
        const dateSet = new Set(symbolSeries[sym].dates);
        commonDates = commonDates.filter((d) => dateSet.has(d));
      }

      if (!commonDates.length) {
        setError(
          "No overlapping dates between the selected symbols."
        );
        setLoading(false);
        return;
      }

      // Build Recharts data array: [{ date, SYMBOL1, SYMBOL2, ... }, ...]
      const combinedData = commonDates.map((d) => {
        const row = { date: d };
        validSymbols.forEach((sym) => {
          const value = symbolSeries[sym].map[d];
          row[sym] =
            typeof value === "number" && !Number.isNaN(value)
              ? Number(value.toFixed(2))
              : null;
        });
        return row;
      });

      setChartData(combinedData);
      setSymbols(validSymbols);
    } catch (err) {
      console.error(err);
      setError("Failed to load data. Please check symbols or server.");
    } finally {
      setLoading(false);
    }
  };

  const finalChartData = useMemo(() => {
    if (!chartData?.length || !symbols?.length) return [];

    const rows =
      selectedRange === "Max"
        ? chartData
        : chartData.slice(Math.max(0, chartData.length - (timeFrames[selectedRange] || Infinity)));

    if (!rows.length) return [];

    // Rebase each symbol to 100 using the FIRST VISIBLE value in this window
    const base = {};
    symbols.forEach((sym) => {
      const firstRowWithValue = rows.find((r) => Number.isFinite(r[sym]));
      base[sym] = firstRowWithValue ? firstRowWithValue[sym] : null;
    });

    return rows.map((r) => {
      const out = { date: r.date };
      symbols.forEach((sym) => {
        const v = r[sym];
        const b = base[sym];
        out[sym] =
          Number.isFinite(v) && Number.isFinite(b) && b !== 0
            ? Number(((v / b) * 100).toFixed(2))
            : null;
      });
      return out;
    });
  }, [chartData, symbols, selectedRange]);

  // Correlation info (only when there are exactly 2 symbols)
  const correlationInfo = useMemo(() => {
    if (symbols.length !== 2 || finalChartData.length < 10) return null;

    const [s1, s2] = symbols;

    // Overall correlation over visible range
    const overall = computeCorrelationFromRows(finalChartData, s1, s2);

    // Window-based correlations (approx by trading days)
    const windowConfig = {
      "1M": 22,
      "3M": 66,
      "6M": 132,
      "1Y": 252,
    };

    const windows = {};

    Object.entries(windowConfig).forEach(([label, size]) => {
      if (finalChartData.length > size + 5) {
        const slice = finalChartData.slice(-size);
        windows[label] = computeCorrelationFromRows(slice, s1, s2);
      } else {
        windows[label] = null;
      }
    });

    return { s1, s2, overall, windows };
  }, [symbols, finalChartData]);


  useEffect(() => {
    if (symbols.length === 2) setPairMode("ratio");
  }, [symbols]);

  // Rolling 3M correlation series (aligned with CURRENT visible history)
  const rolling3MData = useMemo(() => {
    if (symbols.length !== 2 || finalChartData.length < 2) return [];

    const [s1, s2] = symbols;
    const rows = finalChartData;

    // Base ~3 months of trading days
    let windowSize = 66;

    // If range is small, shrink window but keep some smoothing
    if (rows.length < windowSize) {
      windowSize = Math.max(5, Math.floor(rows.length * 0.6));
    }

    // Start with full date series but corr = null
    const output = rows.map((row) => ({
      date: row.date,
      corr: null,
    }));

    // Fill corr values only where we have enough history
    for (let endIdx = windowSize - 1; endIdx < rows.length; endIdx++) {
      const slice = rows.slice(endIdx - windowSize + 1, endIdx + 1);
      const corr = computeCorrelationFromRows(slice, s1, s2);

      if (corr !== null && !Number.isNaN(corr)) {
        output[endIdx].corr = Number(corr.toFixed(2));
      }
    }

    return output;
  }, [symbols, finalChartData]);

  const pairSeries = useMemo(() => {
    if (symbols.length !== 2 || finalChartData.length < 5) return null;

    const [s1, s2] = symbols;

    // Ratio of rebased indices => relative performance (starts ~100)
    const ratioRows = finalChartData.map((r) => {
      const a = r[s1];
      const b = r[s2];
      const ratio =
        Number.isFinite(a) && Number.isFinite(b) && b !== 0
          ? Number(((a / b) * 100).toFixed(2))
          : null;
      return { date: r.date, ratio };
    });

    // Rolling correlation already computed (corr field)
    const rollingRows = rolling3MData.map((r) => ({ date: r.date, corr: r.corr }));

    // Z-score of ratio (rolling window ~3M = 66 trading days)
    const win = Math.min(66, Math.max(10, Math.floor(ratioRows.length * 0.4)));
    const zRows = ratioRows.map((r) => ({ date: r.date, z: null }));

    for (let i = win - 1; i < ratioRows.length; i++) {
      const slice = ratioRows.slice(i - win + 1, i + 1).map((x) => x.ratio).filter(Number.isFinite);
      if (slice.length < Math.max(8, Math.floor(win * 0.6))) continue;

      const mean = slice.reduce((a, v) => a + v, 0) / slice.length;
      const variance = slice.reduce((a, v) => a + (v - mean) ** 2, 0) / slice.length;
      const std = Math.sqrt(variance);

      const cur = ratioRows[i].ratio;
      if (Number.isFinite(cur) && std > 0) {
        zRows[i].z = Number(((cur - mean) / std).toFixed(2));
      }
    }

    return { s1, s2, ratioRows, rollingRows, zRows, zWin: win };
  }, [symbols, finalChartData, rolling3MData]);



  const tooltipFormatter = (value, name) => {
    // value is rebased to 100, so show % change from 100
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const pctChange = num - 100;
    const sign = pctChange >= 0 ? "+" : "";
    return [`${num.toFixed(2)} (${sign}${pctChange.toFixed(2)}%)`, name];
  };

  const YAxisWithCorrLabel = ({ viewBox, corr }) => {
    const { x, y, height } = viewBox;

    const AXIS_OFFSET = 70; // 👈 moves content into chart

    const corrText =
      corr != null && Number.isFinite(corr)
        ? `Correlation (view): ${formatCorrelation(corr)}`
        : null;

    return (
      <g>
        {/* Vertical axis title */}
        <text
          x={x + AXIS_OFFSET}
          y={y + height / 2}
          transform={`rotate(-90 ${x + AXIS_OFFSET} ${y + height / 2})`}
          textAnchor="middle"
          fontSize={10}
          fill="#374151"
        >
          Rebased to 100
        </text>

        {/* Correlation badge */}
        {corrText && (
          <>
            <rect
              x={x + AXIS_OFFSET - 6}
              y={y + 6}
              width={160}
              height={20}
              rx={6}
              fill="#ffffff"
              opacity={0.85}
            />
            <text
              x={x + AXIS_OFFSET}
              y={y + 20}
              fontSize={14}
              fontWeight={700}
              fill={
                corr >= 0.5 ? "#16a34a" :
                corr <= -0.5 ? "#dc2626" :
                "#111827"
              }
            >
              {corrText}
            </text>
          </>
        )}
      </g>
    );
  };

  const LegendWithLinks = ({ payload }) => {
    if (!payload?.length) return null;

    return (
      <div className="w-full flex justify-center">
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
          {payload.map((entry) => {
            const sym = entry.value;
            const color = entry.color;

            return (
              <span key={sym} className="inline-flex items-center gap-2">
                {/* thicker + longer line marker */}
                <span
                  style={{
                    width: 26,          // longer
                    height: 3,          // thicker
                    backgroundColor: color,
                    borderRadius: 999,
                    display: "inline-block",
                  }}
                />

                {/* colored hyperlink */}
                <a
                  href={`https://scan360.in/symbol/${sym}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color }}
                  className="hover:underline"
                >
                  {sym}
                </a>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const RatioWinnerOverlay = ({ s1, s2 }) => (
    <g>
      {/* Upper zone label */}
      <text
        x={62}
        y={30}
        fontSize={11}
        fontWeight={700}
        fill="#166534"
      >
        ↑ {s1} winning
      </text>

      {/* Lower zone label */}
      <text
        x={62}
        y={45}
        fontSize={11}
        fontWeight={700}
        fill="#991b1b"
      >
        ↓ {s2} winning
      </text>
    </g>
  );

  const ZScoreOverlay = () => (
    <g>
      <text x={72} y={30} fontSize={11} fontWeight={700} fill="#dc2626">
        Z ≥ +2 : Over-extended
      </text>
      <text x={72} y={45} fontSize={11} fontWeight={700} fill="#16a34a">
        Z ≤ −2 : Under-extended
      </text>
    </g>
  );

  return (
    <div className="p-2 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-3">
        Chart PlayGround
      </h1>

      {/* Input Section */}
      <div className="mb-4 space-y-2">
        {/* Checkbox row */}
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={manualMode}
              onChange={(e) => setManualMode(e.target.checked)}
              className="h-4 w-4"
            />
            Enter comma separated symbols
          </label>
        </div>

        {/* Input + button row */}
        <div className="mb-4 space-y-2">
          {/* Chips (can grow, button ignores this) */}
          {!manualMode && selectedSymbols.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedSymbols.map((sym) => (
                <span
                  key={sym}
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs
                            rounded-full bg-blue-50 text-blue-800 border border-blue-200"
                >
                  <span className="font-semibold">{sym}</span>
                  <button
                    type="button"
                    onClick={() => removeSymbolFromInput(sym)}
                    className="text-blue-700 hover:text-blue-900"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* 🔑 Search input + button aligned row */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              {!manualMode ? (
                <div className="relative" ref={sugRef}>
                  <input
                    ref={inputRef}
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm
                              focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={dirLoading ? "Loading symbols..." : "Enter Anything GOLD, RELIANCE, NIFTY, BITCOIN ..."}
                    value={q}
                    disabled={dirLoading}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setOpenSug(true);
                      setActiveIdx(0);
                    }}
                    onFocus={() => q && setOpenSug(true)}
                    onKeyDown={onKeyDown}
                  />

                  {/* dropdown */}
                  {openSug && suggestions.length > 0 && (
                    <div
                      className="absolute z-20 left-0 mt-1 w-full overflow-auto
                                bg-white border border-gray-200 rounded-md shadow-lg
                                max-h-64 sm:max-h-80"
                    >
                      {suggestions.map((sug, idx) => (
                        <button
                          key={sug.symbol}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addSymbolToInput(sug.symbol);
                          }}
                          className={`w-full text-left px-3 py-3 sm:py-2 text-sm ${
                            idx === activeIdx ? "bg-blue-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex justify-between gap-3">
                            <span className="font-medium text-gray-900 truncate">
                              {sug.name}
                            </span>
                            <span className="text-gray-500 shrink-0">
                              {sug.quoteType}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm
                            focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. TATAMOTORS, HDFCBANK, RELIANCE"
                  value={symbolsInput}
                  onChange={(e) => setSymbolsInput(e.target.value)}
                />
              )}
            </div>

            {/* ✅ Mobile: full width button below. Desktop: normal width. */}
            <button
              onClick={handleLoad}
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-semibold rounded
                        bg-blue-600 text-white hover:bg-blue-700
                        disabled:opacity-60"
            >
              {loading ? "Loading..." : "Load Charts"}
            </button>
          </div>

        </div>
      </div>

      {/* Info / Error */}
      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {chartData.length > 0 &&
      <div className="mt-1 mb-2 inline-flex flex-wrap gap-0 border border-gray-300 rounded-md overflow-hidden">
        {Object.keys(timeFrames).map((range, idx, arr) => {
          const isActive = selectedRange === range;
          const isFirst = idx === 0;
          const isLast = idx === arr.length - 1;

          return (
            <button
              key={range}
              onClick={() => setSelectedRange(range)}
              className={`transition-all duration-200 font-medium
                ${isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'}
                ${!isLast ? 'border-r border-gray-300' : ''}
                ${isFirst ? 'rounded-l-md' : ''}
                ${isLast ? 'rounded-r-md' : ''}
                px-3 py-1 text-sm sm:px-3.5 sm:py-1 sm:text-sm
              `}
            >
              {range}
            </button>
          );
        })}
      </div>
      }
      {symbols.length > 0 && (
        <div className="mb-2 text-xs text-gray-500">
          Normalised so that value = 100 at the start of the current view (6M/1Y/3Y/etc).
        </div>
      )}

      {/* Chart */}
      <div className="w-full h-[420px] border rounded bg-white shadow-sm p-2">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={finalChartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                domain={["auto", "auto"]}
              >
                <Label
                  position="insideLeft"
                  content={
                    <YAxisWithCorrLabel corr={correlationInfo?.overall} />
                  }
                />
              </YAxis>
              <Tooltip formatter={tooltipFormatter} />
              <Legend
                content={<LegendWithLinks />}
                verticalAlign="bottom"
              />
              {symbols.map((sym, idx) => (
                <Line
                  key={sym}
                  type="monotone"
                  dataKey={sym}
                  stroke={COLORS[idx % COLORS.length]}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            {loading
              ? "Fetching data..."
              : "Enter symbols and click 'Load Charts' to view the graph."}
          </div>
        )}
      </div>

      {/* Pair Analysis (only when there are exactly 2 symbols) */}
      {symbols.length === 2 && pairSeries && (
        <div className="mt-3">
          {/* Toggle buttons */}
          <div className="inline-flex flex-wrap gap-0 border border-gray-300 rounded-md overflow-hidden">
            {[
              ["ratio", "Ratio"],
              ["rolling", "Rolling Corr"],
              ["zscore", "Z-Score"],
            ].map(([key, label], idx, arr) => {
              const isActive = pairMode === key;
              const isFirst = idx === 0;
              const isLast = idx === arr.length - 1;

              return (
                <button
                  key={key}
                  onClick={() => setPairMode(key)}
                  className={`transition-all duration-200 font-medium
                    ${isActive ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}
                    ${!isLast ? "border-r border-gray-300" : ""}
                    ${isFirst ? "rounded-l-md" : ""}
                    ${isLast ? "rounded-r-md" : ""}
                    px-3 py-1 text-sm`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Helper tip */}
          <div className="mt-2 mb-1 text-xs text-gray-600">
            {pairMode === "ratio" && (
              <>
                <span className="font-semibold">Ratio (Relative Strength)</span>:  
                The line is rebased to 100 at the start of the current view.
                <br />
                <span className="text-green-700 font-semibold">{pairSeries.s1}</span> wins when above 100,  
                <span className="text-red-700 font-semibold">{pairSeries.s2}</span> wins when below 100.
              </>
            )}
            {pairMode === "rolling" && (
              <>
                <span className="font-semibold">Rolling correlation</span> shows how tightly the two move together over time
                (here using a ~3-month rolling window). +1 = move together, 0 = unrelated, −1 = opposite.
              </>
            )}
            {pairMode === "zscore" && (
              <>
                <span className="font-semibold">Z-score</span> measures how “stretched” the ratio is versus its recent history
                (rolling window ≈ {pairSeries.zWin} trading days). 0 = normal, +2 = unusually high, −2 = unusually low.
              </>
            )}
          </div>

          {/* Secondary chart */}
          <div className="w-full h-[260px] border rounded bg-white shadow-sm p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={
                  pairMode === "ratio"
                    ? pairSeries.ratioRows
                    : pairMode === "rolling"
                      ? pairSeries.rollingRows
                      : pairSeries.zRows
                }
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={20} />

                <YAxis
                  tick={{ fontSize: 10 }}
                  domain={
                    pairMode === "rolling"
                      ? [-1, 1]
                      : ["auto", "auto"]
                  }
                />
                {pairMode === "ratio" && (
                  <Customized
                    component={() => (
                      <RatioWinnerOverlay
                        s1={pairSeries.s1}
                        s2={pairSeries.s2}
                      />
                    )}
                  />
                )}

                <Tooltip
                  formatter={(v) => {
                    const num = Number(v);
                    if (!Number.isFinite(num)) return v;
                    if (pairMode === "rolling") return [num.toFixed(2), "Corr"];
                    if (pairMode === "zscore") return [num.toFixed(2), "Z"];
                    return [num.toFixed(2), "Ratio"];
                  }}
                />

                {pairMode === "ratio" && (
                  <>
                    {/* Winning zones */}
                    <ReferenceArea
                      y1={100}
                      y2="auto"
                      fill="#dcfce7"   // light green
                      fillOpacity={0.35}
                    />
                    <ReferenceArea
                      y1="auto"
                      y2={100}
                      fill="#fee2e2"   // light red
                      fillOpacity={0.35}
                    />

                    {/* Baseline */}
                    <ReferenceLine
                      y={100}
                      stroke="#000000"
                      strokeWidth={2}
                    />
                  </>
                )}

                {pairMode === "rolling" && (
                  <>
                    {/* Positive correlation zone */}
                    <ReferenceArea
                      y1={0}
                      y2={1}
                      fill="#dcfce7"     // light green
                      fillOpacity={0.25}
                    />

                    {/* Negative correlation zone */}
                    <ReferenceArea
                      y1={-1}
                      y2={0}
                      fill="#fee2e2"     // light red
                      fillOpacity={0.25}
                    />

                    {/* Neutral baseline */}
                    <ReferenceLine
                      y={0}
                      stroke="#000000"
                      strokeWidth={2}
                    />
                  </>
                )}


                {pairMode === "zscore" && (
                  <>
                    {/* Extreme zones */}
                    <ReferenceArea
                      y1={2}
                      y2="auto"
                      fill="#fee2e2"       // red tint
                      fillOpacity={0.35}
                    />
                    <ReferenceArea
                      y1="auto"
                      y2={-2}
                      fill="#dcfce7"       // green tint
                      fillOpacity={0.35}
                    />

                    {/* Mild stretch zones */}
                    <ReferenceArea
                      y1={1}
                      y2={2}
                      fill="#fef9c3"       // yellow
                      fillOpacity={0.25}
                    />
                    <ReferenceArea
                      y1={-2}
                      y2={-1}
                      fill="#fef9c3"
                      fillOpacity={0.25}
                    />

                    {/* Reference lines */}
                    <ReferenceLine y={0} stroke="#000000" strokeWidth={2} />
                    <ReferenceLine y={1} stroke="#9ca3af" strokeDasharray="4 4" />
                    <ReferenceLine y={-1} stroke="#9ca3af" strokeDasharray="4 4" />
                    <ReferenceLine y={2} stroke="#dc2626" strokeDasharray="6 4" />
                    <ReferenceLine y={-2} stroke="#16a34a" strokeDasharray="6 4" />
                  </>
                )}

                {pairMode === "zscore" && (
                  <Customized component={ZScoreOverlay} />
                )}

                <Line
                  type="monotone"
                  dataKey={pairMode === "ratio" ? "ratio" : pairMode === "rolling" ? "corr" : "z"}
                  stroke="#2563eb"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>


        </div>
      )}

    </div>
  );
}

export default Playground;

